import { useFormik } from 'formik';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikSelect } from '@/components/form/fields/FormikSelect/FormikSelect';
import { FormikSwitchToggle } from '@/components/form/fields/FormikSwitchToggle/FormikSwitchToggle';
import { Notification } from '@/components/Notification/Notification';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { PlatformConnectionType } from '@/types/invoiceVaultTypes';
import { VaultAccountType } from '@/types/myVaultTypes';
import { loadVaultAccounts, MY_VAULT_QUERY_KEYS } from '@/bundle/_OrgAdmin/pages/MyVaultPage/api/api';
import { PlatformSettingsPayloadType } from '@/api/v1/organization/invoice-vault/platformApi';
import { INVOICE_LINK_EXPIRY_OPTIONS, LINK_MODE_CARDS } from '../../const/const';
import { updatePlatformSettings } from '../../api/api';
import { ToggleRow } from '../../styles';
import { showSnackbarResponseError } from '@/helpers/helpers';

type PropsType = { platform: PlatformConnectionType; canEdit: boolean; onChanged: () => void };

export const PlatformDefaultsForm = ({ platform, canEdit, onChanged }: PropsType) => {
  const { data: accountsResponse } = useQuery({
    queryKey: [MY_VAULT_QUERY_KEYS.accounts, 'invoice_vault'],
    queryFn: () => loadVaultAccounts({ limit: 100, page: 1 }),
  });
  const accounts: VaultAccountType[] = (accountsResponse?.body?.results ?? []).filter((a) => a.status !== 'closed');

  const save = useMutation({
    mutationFn: updatePlatformSettings,
    onSuccess(response) {
      if (response.error) return showSnackbarResponseError(response.error);
      showSnackbar('Settings saved.');
      onChanged();
    },
  });

  const formik = useFormik<PlatformSettingsPayloadType>({
    enableReinitialize: true,
    initialValues: {
      use_default_account: platform.use_default_account,
      default_vault_account_id: platform.default_vault_account?.id ?? null,
      default_rail: platform.default_rail,
      default_mode: platform.default_mode,
      default_expiry_code: platform.default_expiry_code,
      pay_through_wirevault: platform.pay_through_wirevault,
    },
    onSubmit: (values) => save.mutate(values),
  });

  const account = accounts.find((a) => a.id === formik.values.default_vault_account_id);
  const railOptions = (account?.rail_labels ?? []).map((r) => ({ value: r.code, label: r.label }));

  return (
    <FormikForm value={formik}>
      <Box display='flex' flexDirection='column' marginTop='16px'>
        <Typography variant='subTitle'>Defaults for platform invoices</Typography>
        <ToggleRow>
          <Box>
            <Typography variant='bodySmall' fontWeight='medium'>
              Use a default account
            </Typography>
            <Typography variant='caption'>
              When on, the platform does not need to send an account id with each invoice.
            </Typography>
          </Box>
          <FormikSwitchToggle name='use_default_account' disabled={!canEdit} />
        </ToggleRow>
        {formik.values.use_default_account && (
          <Box display='grid' gridTemplateColumns='1fr 1fr' gap='12px' marginTop='12px'>
            <FormikSelect
              name='default_vault_account_id'
              label='Account'
              options={accounts.map((a) => ({
                value: a.id,
                label: `${a.nickname} · ${a.bank_name} ${a.masked_number}`,
              }))}
              disabled={!canEdit}
            />
            <FormikSelect
              name='default_rail'
              label='Payment type'
              options={railOptions}
              disabled={!canEdit || !account}
            />
          </Box>
        )}
        <Box display='grid' gridTemplateColumns='1fr 1fr' gap='12px' marginTop='12px'>
          <FormikSelect
            name='default_mode'
            label='Link type'
            options={LINK_MODE_CARDS.map((m) => ({ value: m.value, label: m.label }))}
            disabled={!canEdit}
          />
          <FormikSelect
            name='default_expiry_code'
            label='Link expires'
            options={INVOICE_LINK_EXPIRY_OPTIONS}
            disabled={!canEdit}
          />
        </Box>
        <Typography variant='caption'>
          Counted from the invoice due date when the platform sends one, otherwise from creation.
        </Typography>
        <ToggleRow>
          <Box>
            <Typography variant='bodySmall' fontWeight='medium'>
              Pay through WireVault
            </Typography>
            <Typography variant='caption'>
              Lets the payer settle by ACH inside WireVault. Available once My ACH is enabled for your organization.
            </Typography>
          </Box>
          <FormikSwitchToggle name='pay_through_wirevault' disabled />
        </ToggleRow>
        {canEdit && (
          <Box display='flex' justifyContent='flex-end' marginTop='12px'>
            <ButtonNew size='medium' type='submit' isLoading={save.isPending} disabled={!formik.dirty}>
              Save
            </ButtonNew>
          </Box>
        )}
        {!canEdit && <Notification variant='info'>Only master admins change these defaults.</Notification>}
      </Box>
    </FormikForm>
  );
};
